/**
 * components/chip.tsx — the design's `.t-chip` (pill).
 * Tones map to monochrome shades; an optional leading dot (`.t-chip-dot`) is
 * how motion families are distinguished without hue. Variants: lg / solid /
 * outline. A leading icon slot supports the success "✓ approved today" chip.
 */
import React from 'react';
import { View, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { LightColors as C, FontSize as FS, FontWeight as FW } from '../theme/theme';
import { Tone } from '../theme/colors';
import { toneColors } from '../theme/tones';
import { Txt } from './ui';

export interface ChipProps {
  label?: string;
  children?: React.ReactNode;
  tone?: Tone;
  dot?: boolean;
  lg?: boolean;
  solid?: boolean;
  outline?: boolean;
  /** Custom leading node (e.g. an Icon or spinner). */
  leading?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
}

const Chip: React.FC<ChipProps> = ({
  label,
  children,
  tone,
  dot = false,
  lg = false,
  solid = false,
  outline = false,
  leading,
  style,
  textStyle,
}) => {
  const tc = tone ? toneColors(tone) : null;

  let bg: string = C.card;
  let border: string = C.line;
  let fg: string = C.ink2;
  let dotColor: string = C.ink3;

  if (tc) {
    bg = tc.soft;
    border = tc.line;
    fg = tc.fg;
    dotColor = tc.fg;
  }
  if (solid) {
    bg = C.ink;
    border = C.ink;
    fg = C.paper;
  }
  if (outline) {
    bg = 'transparent';
  }

  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 5,
          height: lg ? 26 : 22,
          paddingHorizontal: lg ? 12 : 9,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: border,
          backgroundColor: bg,
        },
        style,
      ]}
    >
      {dot && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: dotColor }} />}
      {leading}
      {label !== undefined ? (
        <Txt size={lg ? FS.sm : FS.xs} weight={FW.medium} color={fg} numberOfLines={1} style={textStyle}>
          {label}
        </Txt>
      ) : (
        children
      )}
    </View>
  );
};

export default Chip;
